import type { TFunction } from "i18next";

import type { TenantPlanOption } from "@/services/authService";
import { serverMessage } from "@/lib/serverMessage";

/**
 * Whether this plan activates directly (`updateTenantPlan`) or has to go
 * through dLocal checkout (`createPlanCheckout`).
 *
 * MIRRORS THE BACKEND GUARD, and must keep mirroring it. `PATCH current/plan`
 * throws "Paid plans require completing checkout" when the tenant-resolved
 * charge is > 0 and no partner discount covers it
 * (tenants.controller.ts). Anything this function waves onto the
 * activation path that the backend then charges for comes back as a 400 the
 * user reads as "No se pudo guardar".
 *
 * Quote `tenantPrice*`, NEVER `priceMonthly`/`priceAnnual` and never a
 * feature-tier helper. This used to call `isFreePlan()`, which is a
 * PREMIUM-FEATURE gate (it answers "does this tier get the app-brain
 * features?"), not a price test — and it lists `tala` ($513/mo) and `semilla`
 * ($99/mo). Both billable tiers therefore took the activation path and every
 * signup that picked one was hard-blocked at the last onboarding step until
 * they gave up or bought a dearer plan.
 *
 * The two cases that MUST stay on the activation path:
 *  - a tier the 80 UI credit covers outright — dLocal rejects a plan amount
 *    below 1, so there is no checkout to send them to;
 *  - a partner-covered remainder — `updateTenantPlan` mints its own dLocal
 *    payment link for the rest, which `createPlanCheckout` cannot do (it has
 *    no partner-discount awareness and would charge full price).
 */
export function planUsesActivationPath(
  planId: string,
  billingInterval: "monthly" | "annual",
  plans: TenantPlanOption[],
): boolean {
  const plan = plans.find((p) => p.id === planId);
  // Unknown plan (catalog not loaded yet, or an id the tenant was never
  // offered): checkout is the safe default. Guessing "free" here would hand
  // out a paid tier for nothing if the backend guard ever loosened.
  if (!plan) return false;

  // null annual means annual is not offered to this tenant (the 80 UI credit
  // accrues monthly, so benefit tenants are billed monthly only). The toggle
  // is hidden for them; if one arrives here anyway, checkout is the honest
  // answer rather than a free activation.
  const tenantPrice =
    billingInterval === "annual" ? plan.tenantPriceAnnual : plan.tenantPriceMonthly;

  if (tenantPrice === 0) return true;
  return (plan.partnerDiscount ?? 0) > 0;
}

/**
 * Turns a failed plan save into something the user — and the next person
 * reading a bug report — can act on.
 *
 * The call sites used to be a bare `catch {}` behind a generic "No se pudo
 * guardar. Intentá de nuevo." That is how a deterministic routing bug
 * (billable tiers sent down the activation path, rejected 400 by the backend)
 * survived three weeks in production: the toast named nothing, and Nest does
 * not log 400s, so neither the user nor the logs could say what broke.
 */
export function resolvePlanSaveErrorMessage(
  error: unknown,
  t: TFunction,
): string {
  return serverMessage(error) ?? t("tenants.edit.errorUpdate");
}
